import { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo } from 'react'

import { useQuery } from '@/api/hooks'
import { userPreferenceDefaults } from '@/defaults'
import MainLoader from '@components/MainLoader/MainLoader'
import { Role, UserColumnPref } from '@domain'
import useUpdatePreferences from '@hooks/useUpdatePreferences'
import UsersApi, { UsersKey } from '@services/users.api'
import { toast } from 'react-hot-toast'

import { colPrefsAreValid, mergePreferences } from './MeProvider.utils'

interface Me {
	id: string
	firstName: string
	lastName: string
	displayName: string
	email: string
	status: string
	enabled: boolean
	roles: Role[]
	permissions: string[]
	isSuperAdmin: boolean
	preferences: Record<string, any>
	profilePic: string
}

const MeContext = createContext<Me | null>(null)

/**
 * Provide a context for the current user.
 */
export default function MeProvider({ children }: PropsWithChildren) {
	const { data } = useQuery({
		queryKey: UsersKey.me,
		queryFn: () => UsersApi.me(),
	})

	const { mutate: updateColPreferences } = useUpdatePreferences()

	// Resets user col prefs to default if saved preferences do not match columns defined in default
	useEffect(() => {
		if (!data?.userPreferences) return

		const purchaseListColumnPref = data?.userPreferences?.find(
			(pref) => pref.key === 'purchaseListColumns'
		)

		if (!purchaseListColumnPref) {
			return
		}

		const parsedColPref: UserColumnPref[] = JSON.parse(purchaseListColumnPref.value as string)

		if (!purchaseListColumnPref) return

		const purchaseListColumnDefault = userPreferenceDefaults.find(
			(pref) => pref.key === 'purchaseListColumns'
		).value as UserColumnPref[]

		const validColPrefs = colPrefsAreValid(parsedColPref, purchaseListColumnDefault)

		if (!validColPrefs) {
			updateColPreferences(
				{
					userPreferences: [
						{ key: 'purchaseListColumns', value: JSON.stringify(purchaseListColumnDefault) },
					],
				},
				{
					onSuccess: () => {
						toast(
							'The columns in the purchases table were updated. Your saved order and hidden columns may have changed',
							{ duration: Infinity }
						)
					},
				}
			)
		}
	}, [data?.userPreferences, updateColPreferences])

	const user = useMemo(() => {
		let preferences = {}
		if (data) {
			const merged = mergePreferences(data.userPreferences)
			preferences = Object.fromEntries(merged.map((t) => [t.key, t.value]))
		}
		return {
			id: data?.id || '',
			firstName: data?.firstName || '',
			lastName: data?.lastName || '',
			displayName: data?.displayName || '',
			email: data?.email || '',
			status: data?.status || '',
			enabled: data?.enabled || false,
			roles: data?.roles || [],
			permissions: data?.permissions || [],
			isSuperAdmin: data?.isSuperAdmin || false,
			preferences: preferences || {},
			profilePic: data?.profilePic || '',
		}
	}, [data])

	if (!data) return <MainLoader />

	return <MeContext.Provider value={user}>{children}</MeContext.Provider>
}

type MeMockedProviderProps = {
	value: Me
	children: ReactNode
}

export function MeMockedProvider({ value, children }: MeMockedProviderProps) {
	return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}

export const useMe = () => {
	if (!MeContext) throw new Error('useMe must be used within a MeProvider')
	return useContext(MeContext)
}
